import {get} from 'lodash';
import React from 'react';
import {Helmet} from 'react-helmet';

import {title} from 'lib/head';
import {ERROR_TYPES} from 'client/constants';
// Deep imports (not the @albert-io/atomic barrel): this page is rendered by the
// Honeybadger error boundary, which sits beneath SessionStore — importing the barrel
// here creates a module cycle back through atomic/organisms/Footer.
import Button from '@albert-io/atomic/atoms/Button/Button.react';
import Grid from '@albert-io/atomic/atoms/Grid/Grid.react';
import Logo from '@albert-io/atomic/atoms/Logo/Logo.react';
import Text from '@albert-io/atomic/atoms/Text/Text.react';

import error404Image from './assets/Error-404-Blue.png';
import errorUnauthorizedImage from './assets/Error-UnauthorizedAccess-Blue.png';
import errorGenericImage from './assets/Error-Generic-Blue.png';

const ERROR_DATA = {
  [ERROR_TYPES.NOT_FOUND]: {
    text: `Looks like the page you are looking for doesn't exist or may have been moved.`,
    image: error404Image
  },
  [ERROR_TYPES.GENERIC]: {
    text: `Oops! Looks like something went wrong.`,
    image: errorGenericImage
  },
  [ERROR_TYPES.UNAUTHORIZED]: {
    text: `Looks like you don't have access to this page, please sign-in and try again.`,
    image: errorUnauthorizedImage
  }
};

/**
 * The error page component sources an `errorCode` from the direct prop or query parameter (`errorCode`).
 * Exposing a prop, but also using a query parameter let us use this component INLINE, but also
 * at a route level where it can be parameterized.
 *
 * In all callsites it is *recommended* to use the `ERROR_TYPES` constants instead of a "raw" number.
 *
 * @returns {React.Component} The `<ErrorPage>` component
 *
 * @example ```
 *  history.pushState(null, `/error?errorCode=${ERROR_TYPES.UNAUTHORIZED}`);
 * ```
 * @example ```
 *  <ErrorPage errorCode={ERROR_TYPES.NOT_FOUND} />
 * ```
 */
interface Props {
  errorCode?: string | number;
  errorText?: string;
  location?: {
    query?: {
      errorCode?: string | number;
    };
  };
}

export default class ErrorPage extends React.PureComponent<Props> {
  getErrorCodeFromProps = () => {
    return (
      this.props.errorCode ||
      get(this.props, ['location', 'query', 'errorCode'], ERROR_TYPES.GENERIC)
    );
  };

  render() {
    const defaultTitle =
      'Albert | Learn by doing | Grades 5-12 reading, writing, math, science, and test prep';
    const errorCode = this.getErrorCodeFromProps();
    const errorData = Object.prototype.hasOwnProperty.call(ERROR_DATA, errorCode)
      ? ERROR_DATA[errorCode]
      : ERROR_DATA[ERROR_TYPES.GENERIC];

    return (
      <Grid.Container className='text-center'>
        <Helmet>{title(defaultTitle)}</Helmet>
        <Grid.Col>
          <Grid.Row align='center' justify='center'>
            <Logo color='blue' width='200px' />
          </Grid.Row>
          <Grid.Row align='center' justify='center'>
            <img alt='' className='h-x20 m-x4 w-auto' src={errorData.image} />
          </Grid.Row>
          <Grid.Row align='center' justify='center'>
            <Text size='l' paragraph>
              {this.props.errorText || errorData.text}
            </Text>
          </Grid.Row>
          <Grid.Row align='center' justify='center'>
            <Button as='a' href='/'>
              Return home
            </Button>
          </Grid.Row>
        </Grid.Col>
      </Grid.Container>
    );
  }
}
